import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API } from '../utils/constants';
import IntakePhotoThumbnails from '../components/shared/IntakePhotoThumbnails';

const CustomerCareHomePage = ({ onNavigate, onBackToGears, currentUser }) => {
  const enabledTools = currentUser?.company?.tools_enabled?.['customer-care'];
  const isToolEnabled = (toolId) => !enabledTools || enabledTools.includes(toolId);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white text-gray-800">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <button onClick={onBackToGears} className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium">
            ← Back to Company Gears
          </button>
        </div>
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2 flex items-center gap-3">
            <span className="text-4xl">🤝</span><span>Customer Care</span>
          </h1>
          <p className="text-gray-600 max-w-2xl text-sm sm:text-base">
            Customer Intake System helps your admin team handle calls professionally,
            extract the right details, and set clear expectations for estimators.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {isToolEnabled('lead-intake') && (
          <button onClick={() => onNavigate('lead-intake')} className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-8 text-white text-left hover:shadow-xl transition-all transform hover:scale-105" data-testid="customer-care-lead-intake-tool-btn">
            <div className="text-5xl mb-4">🧾</div>
            <h2 className="text-2xl font-bold mb-2">Lead Intake</h2>
            <p className="text-green-100 text-sm">Fast intake for leads when you don&apos;t need the full phone script.</p>
          </button>
          )}
          {isToolEnabled('custom-lead-forms') && (
          <button onClick={() => onNavigate('custom-lead-forms')} className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl p-8 text-white text-left hover:shadow-xl transition-all transform hover:scale-105" data-testid="customer-care-custom-lead-forms-tool-btn">
            <div className="text-5xl mb-4">🧩</div>
            <h2 className="text-2xl font-bold mb-2">Custom Lead Forms</h2>
            <p className="text-indigo-100 text-sm">Create simple custom lead forms and sync every submission to CRM + client records.</p>
          </button>
          )}
          {isToolEnabled('phone-intake') && (
          <button onClick={() => onNavigate('phone-intake')} className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-8 text-white text-left hover:shadow-xl transition-all transform hover:scale-105" data-testid="customer-care-guided-intake-tool-btn">
            <div className="text-5xl mb-4">☎️</div>
            <h2 className="text-2xl font-bold mb-2">Guided Intake</h2>
            <p className="text-blue-100 text-sm">Guided, step-by-step script for handling inbound calls.</p>
          </button>
          )}
          {isToolEnabled('phone-intake-archive') && (
          <button onClick={() => onNavigate('phone-intake-archive')} className="bg-white rounded-xl p-8 border border-gray-200 text-left hover:shadow-xl transition-all transform hover:scale-105" data-testid="customer-care-intake-archive-tool-btn">
            <div className="text-4xl mb-3">📂</div>
            <h2 className="text-xl font-semibold mb-2 text-gray-800">Intake Archive</h2>
            <p className="text-gray-600 text-sm">Browse previous intakes, including custom lead form submissions.</p>
          </button>
          )}
          {isToolEnabled('sops') && (
          <button onClick={() => onNavigate('sops')} className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-8 text-white text-left hover:shadow-xl transition-all transform hover:scale-105" data-testid="customer-care-sops-tool-btn">
            <div className="text-5xl mb-4">📋</div>
            <h2 className="text-2xl font-bold mb-2">SOPs</h2>
            <p className="text-orange-100 text-sm">Standard Operating Procedures for Customer Care.</p>
          </button>
          )}
        </div>

      </div>
    </div>
  );
};

// Customer Care - Phone Intake Module
const PhoneIntakeModule = ({ onBack, currentUser, initialIntake, hideScripts = false, hideDivisionPhotoPrompt = false, hidePhotosReminder = false, intakeType = 'phone' }) => {
  // Optional: if opened from archive with existing intake

  const steps = hideScripts
    ? [
        { id: 1, key: 'intake', title: '1. Lead Intake Setup' },
        { id: 2, key: 'contact', title: '2. Contact Info' },
        { id: 3, key: 'division', title: '3. Division Questions' },
        { id: 4, key: 'photos', title: '4. Photos' },
        { id: 5, key: 'wrapup', title: '5. Ending & Handoff' },
      ]
    : [
        { id: 1, key: 'intake', title: '1. Intake Script' },
        { id: 2, key: 'contact', title: '2. Contact Info' },
        { id: 3, key: 'division', title: '3. Division Questions' },
        { id: 4, key: 'photos', title: '4. Photos & Info' },
        { id: 5, key: 'wrapup', title: '5. Ending & Handoff' },
      ];

  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [intakeId, setIntakeId] = useState(initialIntake?.id || null);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [estimatorOption, setEstimatorOption] = useState('');
  const [customEstimatorEmail, setCustomEstimatorEmail] = useState('');
  const [isUploadingPhotos, setIsUploadingPhotos] = useState(false);
  const [formData, setFormData] = useState(() => ({
    division: initialIntake?.division || '',
    name: initialIntake?.name || '',
    phone: initialIntake?.phone || '',
    email: initialIntake?.email || '',
    address: initialIntake?.address || '',
    city: initialIntake?.city || '',
    province: initialIntake?.province || '',
    postal_code: initialIntake?.postal_code || '',
    issue_summary: initialIntake?.issue_summary || '',
    priority: initialIntake?.priority || 'Medium',
    photos_received: initialIntake?.photos_received || 'No',
    uploaded_files: initialIntake?.uploaded_files || [],
    foundations: initialIntake?.foundations || {
      type_of_issue: [],
      structure_type: '',
      crawlspace_headroom: '',
      severity_notes: '',
      foundation_type: '',
      issue_location: '',
      is_area_finished: '',
      additional_notes: '',
      exterior_obstructions: '',
    },
    waterproofing: {
      system_type: '',
      has_sump_pit: '',
      has_sump_pump: '',
      water_timing: '',
      foundation_type: '',
      leaking_walls: '',
      exterior_obstacles: '',
      exterior_access: '',
      universal_questions_notes: '',
      ...(initialIntake?.waterproofing || {}),
    },
    septics: initialIntake?.septics || {
      request_type: '',
      bedrooms: '',
      bathrooms: '',
      square_footage: '',
      soil_type: '',
      lot_size: '',
      urgency_notes: '',
    },
    notes_for_estimator: initialIntake?.notes_for_estimator || '',
    estimator_email: initialIntake?.estimator_email || '',
  }));

  // Simple estimator options list - replace addresses as needed
  const [knownEstimators, setKnownEstimators] = useState(() => {
    try {
      const fromStorage = localStorage.getItem('knownEstimators');
      if (fromStorage) {
        return JSON.parse(fromStorage);
      }
    } catch (e) {
      console.error('Failed to parse knownEstimators from localStorage', e);
    }
    return [];
  });

  const [estimatorInput, setEstimatorInput] = useState(formData.estimator_email || '');
  const [sendStatus, setSendStatus] = useState(null); // 'success' | 'error' | null

  // Check if the form has any meaningful user-entered data
  const hasUserData = () => {
    return !!(
      formData.division ||
      formData.name?.trim() ||
      formData.phone?.trim() ||
      formData.email?.trim() ||
      formData.address?.trim() ||
      formData.city?.trim() ||
      formData.issue_summary?.trim() ||
      formData.notes_for_estimator?.trim()
    );
  };

  // Debounced autosave when formData changes
  useEffect(() => {
    // Skip autosave if no meaningful data and no existing intake
    if (!intakeId && !hasUserData()) return;

    const timeout = setTimeout(async () => {
      try {
        setIsSaving(true);
        setSaveError(null);

        if (!intakeId) {
          // Create new intake
          const createPayload = {
            intake_type: intakeType,
            division: formData.division,
            name: formData.name,
            phone: formData.phone,
            email: formData.email,
            address: formData.address,
            city: formData.city,
            province: formData.province,
            postal_code: formData.postal_code,
            priority: formData.priority,
            photos_received: formData.photos_received,
            foundations: formData.foundations,
            waterproofing: formData.waterproofing,
            septics: formData.septics,
            issue_summary: formData.issue_summary,
            notes_for_estimator: formData.notes_for_estimator,
            estimator_email: formData.estimator_email,
          };
          const res = await axios.post(`${API}/intakes`, createPayload);
          setIntakeId(res.data.id);
        } else {
          // Update existing intake
          const updatePayload = {
            intake_type: intakeType,
            division: formData.division,
            name: formData.name,
            phone: formData.phone,
            email: formData.email,
            address: formData.address,
            city: formData.city,
            province: formData.province,
            postal_code: formData.postal_code,
            priority: formData.priority,
            photos_received: formData.photos_received,
            foundations: formData.foundations,
            waterproofing: formData.waterproofing,
            septics: formData.septics,
            issue_summary: formData.issue_summary,
            notes_for_estimator: formData.notes_for_estimator,
            estimator_email: formData.estimator_email,
          };
          await axios.put(`${API}/intakes/${intakeId}`, updatePayload);
        }

        setLastSavedAt(new Date());

        // Optional: could be used later to distinguish phone vs lead intakes
        if (intakeType === 'lead') {
          // No schema change; this is just a marker we could extend in backend later if needed
        }
      } catch (err) {
        console.error('Error autosaving intake', err);
        setSaveError('Autosave failed. Changes may not be saved.');
      } finally {
        setIsSaving(false);
      }
    }, 1200); // 1.2s debounce

    return () => clearTimeout(timeout);
  }, [formData, intakeId]);

  const goToStep = (index) => {
    if (index >= 0 && index < steps.length) {
      setActiveStepIndex(index);
    }
  };

  // Manual save function - immediate save without debounce
  const handleManualSave = async () => {
    if (!intakeId && !hasUserData()) {
      alert('Please enter some information before saving.');
      return;
    }
    try {
      setIsSaving(true);
      setSaveError(null);

      const payload = {
        intake_type: intakeType,
        division: formData.division,
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        address: formData.address,
        city: formData.city,
        province: formData.province,
        postal_code: formData.postal_code,
        priority: formData.priority,
        photos_received: formData.photos_received,
        foundations: formData.foundations,
        waterproofing: formData.waterproofing,
        septics: formData.septics,
        issue_summary: formData.issue_summary,
        notes_for_estimator: formData.notes_for_estimator,
        estimator_email: formData.estimator_email,
      };

      if (!intakeId) {
        const res = await axios.post(`${API}/intakes`, payload);
        setIntakeId(res.data.id);
      } else {
        await axios.put(`${API}/intakes/${intakeId}`, payload);
      }

      setLastSavedAt(new Date());
    } catch (err) {
      console.error('Error saving intake', err);
      setSaveError('Failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleNext = () => {
    if (activeStepIndex < steps.length - 1) {
      setActiveStepIndex((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (activeStepIndex > 0) {
      setActiveStepIndex((prev) => prev - 1);
    }
  };

  const toggleFoundationIssue = (value) => {
    setFormData((prev) => {
      const existing = prev.foundations.type_of_issue || [];
      const has = existing.includes(value);
      return {
        ...prev,
        foundations: {
          ...prev.foundations,
          type_of_issue: has
            ? existing.filter((v) => v !== value)
            : [...existing, value],
        },
      };
    });
  };

  const renderIntakeStep = () => (
    <div className="space-y-6">
      {!hideScripts && (
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h2 className="text-lg font-semibold mb-3 text-gray-900">
            Opening Script
          </h2>
          <p className="text-sm text-gray-700 mb-3">
            "Hi, thank you for calling, this is _____. How can I help you today?"
          </p>
          <p className="text-xs text-gray-500">
            Let the customer talk fully. Listen carefully for which division this call belongs to.
          </p>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h3 className="text-sm font-semibold mb-2 text-gray-900">Select Division</h3>
          <p className="text-xs text-gray-500 mb-3">
            Pick the division that best matches what the customer is describing.
          </p>
          <div className="space-y-2 text-sm">
            {['Foundations', 'Waterproofing', 'Septics'].map((div) => {
              const selected = formData.division === div;
              return (
                <button
                  key={div}
                  type="button"
                  onClick={() => setFormData({ ...formData, division: div })}
                  className="flex items-center gap-2 cursor-pointer bg-transparent border-none p-0 m-0"
                  aria-pressed={selected}
                >
                  <span
                    className={`inline-flex items-center justify-center h-4 w-4 rounded-full border-2 transition-colors duration-150 ${
                      selected
                        ? 'border-blue-600 bg-blue-600'
                        : 'border-gray-400 bg-white'
                    }`}
                  />
                  <span>{div}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h3 className="text-sm font-semibold mb-2 text-gray-900">Common Services We Do NOT Offer</h3>
          <ul className="list-disc list-inside text-xs text-gray-700 space-y-1">
            <li>Septic pumping</li>
            <li>Concrete slab lifting</li>
            <li>Crawl space encapsulation</li>
            <li>Insulation</li>
            <li>Parging</li>
          </ul>
          <p className="text-xs text-gray-500 mt-3">
            If they ask for something here, calmly explain that we don&apos;t currently offer that
            service and offer to check for a recommended contact.
          </p>
        </div>
      </div>

      {!hideScripts && (
        <>
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <h3 className="text-sm font-semibold mb-2 text-gray-900">Set Expectation for Virtual Estimating</h3>
            <p className="text-xs text-gray-500 mb-2">Read this confidently, not apologetically:</p>
            <p className="text-sm text-gray-700 mb-3">
              "Just so you know, we start nearly all projects with a virtual estimate. It saves time,
              avoids unnecessary site visits, and lets our estimator see if the project needs an
              in-person follow-up. If the situation requires it, our estimator will absolutely
              schedule an onsite review."
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
            <h3 className="text-sm font-semibold mb-2 text-gray-900">Move to Contact Information</h3>
            <p className="text-xs text-gray-600 mb-1">Use this to smoothly transition into Step 2:</p>
            <p className="text-sm text-gray-800">
              "Let me start by getting your contact information and property location."
            </p>
          </div>
        </>
      )}
    </div>
  );

  const renderContactStep = () => (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <h2 className="text-lg font-semibold mb-3 text-gray-900">
          Contact Information
        </h2>
        <p className="text-sm text-gray-700 mb-4">
          "Can I get your name, phone number, email address, and the property address you&apos;re calling about?"
        </p>
        <div className="space-y-3 text-sm">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Phone</label>
            <input
              type="text"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Property Address</label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">City</label>
            <input
              type="text"
              value={formData.city}
              onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Province</label>
            <input
              type="text"
              value={formData.province}
              onChange={(e) => setFormData({ ...formData, province: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Postal Code</label>
            <input
              type="text"
              value={formData.postal_code}
              onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
        </div>
      </div>

      <div className="space-y-3 text-sm">
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h3 className="text-xs font-semibold mb-2 text-gray-900">Priority</h3>
          <select
            value={formData.priority}
            onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
            className="w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
          >
            <option value="Low">Low</option>
            <option value="Medium">Medium</option>
            <option value="High">High</option>
          </select>
          <p className="text-[11px] text-gray-500 mt-2">
            High: active failure, structural risk, flooding.
          </p>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h3 className="text-xs font-semibold mb-2 text-gray-900">Division (summary)</h3>
          <p className="text-sm text-gray-700">
            {formData.division || 'Not selected yet'}
          </p>
        </div>
      </div>

    </div>
  );

  const renderDivisionStep = () => {
    const div = formData.division;

    if (div === 'Foundations') {
      const issues = [
        'Cracked foundation',
        'Bowing wall',
        'Failing / collapsing wall',
        'Sagging floors',
        'Post & beam issues',
        'Crawlspace problems',
        'Water entering foundation',
        'Structural concerns',
        "Unknown — just something's wrong",
      ];

      return (
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <h2 className="text-lg font-semibold mb-1 text-gray-900">Foundations – Question Pack</h2>
            <p className="text-sm text-gray-500 mb-5">
              Walk through each question with the customer, one at a time.
            </p>

            {/* 1. Type of Issue */}
            <div className="mb-5">
              <label className="block text-sm font-semibold text-gray-800 mb-1">1. Type of Issue</label>
              <p className="text-xs text-gray-500 mb-2">"What is the primary concern that prompted your call today?" (Check all that apply)</p>
              <div className="flex flex-col gap-2 text-sm">
                {issues.map((label) => {
                  const checked = formData.foundations.type_of_issue.includes(label);
                  return (
                    <button
                      key={label}
                      type="button"
                      onClick={() => toggleFoundationIssue(label)}
                      className="flex items-center gap-2 text-left bg-transparent border-none p-0 m-0"
                      aria-pressed={checked}
                    >
                      <span
                        className={`inline-flex items-center justify-center h-4 w-4 rounded-full border-2 flex-shrink-0 transition-colors duration-150 ${
                          checked ? 'border-blue-600 bg-blue-600' : 'border-gray-400 bg-white'
                        }`}
                      />
                      <span>{label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* 2. Location of Issue */}
            <div className="mb-5">
              <label className="block text-sm font-semibold text-gray-800 mb-1">2. Location of Issue</label>
              <p className="text-xs text-gray-500 mb-2">
                {formData.foundations.type_of_issue.includes('Sagging floors')
                  ? '"Where exactly is the sagging floor located — which room or area of the home?"'
                  : '"When I am looking at the front of your home, which wall are you concerned about — front, left side, right side, or back?"'}
              </p>
              <input
                type="text"
                value={formData.foundations.issue_location || ''}
                onChange={(e) =>
                  setFormData({ ...formData, foundations: { ...formData.foundations, issue_location: e.target.value } })
                }
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder={
                  formData.foundations.type_of_issue.includes('Sagging floors')
                    ? 'e.g. Main floor living room near centre beam'
                    : 'e.g. Front wall and left side wall'
                }
              />
            </div>

            {/* 3. Summary of Issue */}
            <div className="mb-5">
              <label className="block text-sm font-semibold text-gray-800 mb-1">3. Summary of Issue</label>
              <p className="text-xs text-gray-500 mb-2">Type a brief summary in your own words for the estimator.</p>
              <textarea
                value={formData.issue_summary}
                onChange={(e) => setFormData({ ...formData, issue_summary: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm h-20"
                placeholder="Brief summary in the admin's own words..."
              />
            </div>

            {/* 4. Structure Type */}
            <div className="mb-5">
              <label className="block text-sm font-semibold text-gray-800 mb-1">4. Structure Type</label>
              <p className="text-xs text-gray-500 mb-2">"Do you have a full basement, crawlspace, or dugout?"</p>
              <select
                value={formData.foundations.structure_type}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    foundations: {
                      ...formData.foundations,
                      structure_type: e.target.value,
                      foundation_type: e.target.value === 'Full basement' ? formData.foundations.foundation_type || '' : '',
                    },
                  })
                }
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="">Select...</option>
                <option value="Full basement">Full basement</option>
                <option value="Crawlspace">Crawlspace</option>
                <option value="Dugout">Dugout</option>
              </select>

              {formData.foundations.structure_type === 'Full basement' && (
                <div className="mt-3">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Foundation Type</label>
                  <select
                    value={formData.foundations.foundation_type || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, foundations: { ...formData.foundations, foundation_type: e.target.value } })
                    }
                    className="w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
                  >
                    <option value="">Select...</option>
                    <option value="Block">Block</option>
                    <option value="Poured Concrete">Poured Concrete</option>
                    <option value="ICF (Insulated Concrete)">ICF (Insulated Concrete)</option>
                    <option value="Wood">Wood</option>
                  </select>
                </div>
              )}

              {formData.foundations.structure_type === 'Crawlspace' && (
                <div className="mt-3">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Crawlspace headroom</label>
                  <input
                    type="text"
                    value={formData.foundations.crawlspace_headroom}
                    onChange={(e) =>
                      setFormData({ ...formData, foundations: { ...formData.foundations, crawlspace_headroom: e.target.value } })
                    }
                    className="w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
                    placeholder="Approximate headroom to work under there"
                  />
                </div>
              )}
            </div>

            {/* 5. Is the area finished? */}
            <div className="mb-5">
              <label className="block text-sm font-semibold text-gray-800 mb-1">5. Is the Area Finished?</label>
              <p className="text-xs text-gray-500 mb-2">"Is the foundation wall or basement area finished in the area of concern — drywall, framing, flooring, etc.?"</p>
              <input
                type="text"
                value={formData.foundations.is_area_finished || ''}
                onChange={(e) =>
                  setFormData({ ...formData, foundations: { ...formData.foundations, is_area_finished: e.target.value } })
                }
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder="e.g. Yes fully finished, partially finished, unfinished..."
              />
            </div>

            {/* 6. Exterior Obstructions */}
            <div className="mb-5">
              <label className="block text-sm font-semibold text-gray-800 mb-1">
                6. Exterior Obstructions
                {(['Cracked foundation','Bowing wall','Failing / collapsing wall','Water entering foundation','Structural concerns'].some(i => formData.foundations.type_of_issue.includes(i))) && (
                  <span className="ml-2 text-xs font-normal text-amber-600 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">Important for excavation</span>
                )}
              </label>
              <p className="text-xs text-gray-500 mb-2">"On the outside of that wall, are there any obstructions that could get in the way — things like an AC unit, driveway, deck, landscaping, a tight lot line, or a fence?"</p>
              <textarea
                value={formData.foundations.exterior_obstructions || ''}
                onChange={(e) =>
                  setFormData({ ...formData, foundations: { ...formData.foundations, exterior_obstructions: e.target.value } })
                }
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm h-16"
                placeholder="e.g. Driveway along front wall, AC unit on left side, deck along back..."
              />
            </div>

            {/* 7. Severity & Symptoms */}
            <div className="mb-5">
              <label className="block text-sm font-semibold text-gray-800 mb-1">7. Severity &amp; Symptoms</label>
              <p className="text-xs text-gray-500 mb-2">Ask: Is it getting worse? Any sticking doors or windows? Visible shifting or cracks? Any moisture present?</p>
              <textarea
                value={formData.foundations.severity_notes}
                onChange={(e) =>
                  setFormData({ ...formData, foundations: { ...formData.foundations, severity_notes: e.target.value } })
                }
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm h-20"
                placeholder="Notes on severity, movement, moisture, sticking doors..."
              />
            </div>

            {/* 8. Additional Notes */}
            <div className="mb-2">
              <label className="block text-sm font-semibold text-gray-800 mb-1">8. Additional Notes</label>
              <p className="text-xs text-gray-500 mb-2">How long has this been happening? Has anything been done previously? Any other relevant details?</p>
              <textarea
                value={formData.foundations.additional_notes || ''}
                onChange={(e) =>
                  setFormData({ ...formData, foundations: { ...formData.foundations, additional_notes: e.target.value } })
                }
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm h-20"
                placeholder="Any extra notes from the conversation..."
              />
            </div>
          </div>

          {!hideDivisionPhotoPrompt && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 text-sm text-blue-900">
              <h3 className="font-semibold mb-1">Photo Prompt</h3>
              <p>
                "Foundation work is very visual — if you can send photos to {currentUser?.company?.email || 'our office'}, our estimator
                can review it much faster."
              </p>
            </div>
          )}
        </div>
      );
    }

    if (div === 'Waterproofing') {
      return (
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <h2 className="text-lg font-semibold mb-1 text-gray-900">Waterproofing – Question Pack</h2>
            <p className="text-sm text-gray-500 mb-5">
              Walk through each question with the customer, one at a time.
            </p>

            {/* 1. Summary */}
            <div className="mb-5">
              <label className="block text-sm font-semibold text-gray-800 mb-1">1. Summary of Issue</label>
              <p className="text-xs text-gray-500 mb-2">Type a brief summary in your own words for the estimator.</p>
              <textarea
                value={formData.issue_summary}
                onChange={(e) => setFormData({ ...formData, issue_summary: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm h-20"
                placeholder="Brief summary in the admin's own words..."
              />
            </div>

            {/* 2. Leaking walls */}
            <div className="mb-5">
              <label className="block text-sm font-semibold text-gray-800 mb-1">2. What walls are currently leaking?</label>
              <p className="text-xs text-gray-500 mb-2">"When I am looking at the front of your home, is it the front, left side, right side, back?"</p>
              <input
                type="text"
                value={formData.waterproofing.leaking_walls || ''}
                onChange={(e) => setFormData({ ...formData, waterproofing: { ...formData.waterproofing, leaking_walls: e.target.value } })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder="e.g. Front and left side"
              />
            </div>

            {/* 3. System type */}
            <div className="mb-5">
              <label className="block text-sm font-semibold text-gray-800 mb-1">3. Interior or exterior waterproofing?</label>
              <p className="text-xs text-gray-500 mb-2">"Are you looking for an interior or exterior waterproofing system, or are you not sure yet?"</p>
              <select
                value={formData.waterproofing.system_type}
                onChange={(e) => setFormData({ ...formData, waterproofing: { ...formData.waterproofing, system_type: e.target.value } })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="">Select...</option>
                <option value="Interior">Interior</option>
                <option value="Exterior">Exterior</option>
                <option value="Not Sure">Not Sure</option>
                <option value="Both">Both</option>
              </select>
            </div>

            {/* 4. Sump pit */}
            <div className="mb-5">
              <label className="block text-sm font-semibold text-gray-800 mb-1">4. Existing sump pit?</label>
              <select
                value={formData.waterproofing.has_sump_pit}
                onChange={(e) => setFormData({ ...formData, waterproofing: { ...formData.waterproofing, has_sump_pit: e.target.value } })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="">Select...</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
                <option value="Unknown">Unknown</option>
              </select>
            </div>

            {/* 5. Sump pump */}
            <div className="mb-5">
              <label className="block text-sm font-semibold text-gray-800 mb-1">5. Working sump pump?</label>
              <select
                value={formData.waterproofing.has_sump_pump}
                onChange={(e) => setFormData({ ...formData, waterproofing: { ...formData.waterproofing, has_sump_pump: e.target.value } })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="">Select...</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
                <option value="Unknown">Unknown</option>
              </select>
            </div>

            {/* 6. When does water enter */}
            <div className="mb-5">
              <label className="block text-sm font-semibold text-gray-800 mb-1">6. When does water enter?</label>
              <input
                type="text"
                value={formData.waterproofing.water_timing}
                onChange={(e) => setFormData({ ...formData, waterproofing: { ...formData.waterproofing, water_timing: e.target.value } })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder="Rain, snowmelt, all the time..."
              />
            </div>

            {/* 7. Foundation type */}
            <div className="mb-5">
              <label className="block text-sm font-semibold text-gray-800 mb-1">7. Foundation type</label>
              <input
                type="text"
                value={formData.waterproofing.foundation_type}
                onChange={(e) => setFormData({ ...formData, waterproofing: { ...formData.waterproofing, foundation_type: e.target.value } })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder="Block, poured concrete, wood, etc."
              />
            </div>

            {/* 8. Obstacles along wall */}
            <div className="mb-5">
              <label className="block text-sm font-semibold text-gray-800 mb-1">8. Obstacles along wall</label>
              <textarea
                value={formData.waterproofing.exterior_obstacles}
                onChange={(e) => setFormData({ ...formData, waterproofing: { ...formData.waterproofing, exterior_obstacles: e.target.value } })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm h-16"
                placeholder="Deck, driveway, AC unit, porch, landscaping, tight lot line, fence..."
              />
            </div>

            {/* 9. Access around home */}
            <div className="mb-5">
              <label className="block text-sm font-semibold text-gray-800 mb-1">9. Access around home</label>
              <input
                type="text"
                value={formData.waterproofing.exterior_access}
                onChange={(e) => setFormData({ ...formData, waterproofing: { ...formData.waterproofing, exterior_access: e.target.value } })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder="Can we get all the way around the home?"
              />
            </div>

            {/* 10. Universal questions & notes */}
            <div className="mb-2">
              <label className="block text-sm font-semibold text-gray-800 mb-1">10. Additional Notes</label>
              <p className="text-xs text-gray-500 mb-2">How long has this been happening? Is it active or occasional? Has the weeping tile ever been replaced?</p>
              <textarea
                value={formData.waterproofing.universal_questions_notes || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    waterproofing: { ...formData.waterproofing, universal_questions_notes: e.target.value },
                  })
                }
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm h-20"
                placeholder="Any extra notes from the conversation..."
                data-testid="waterproofing-universal-notes-textarea"
              />
            </div>
          </div>

          {!hideDivisionPhotoPrompt && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 text-sm text-blue-900">
              <h3 className="font-semibold mb-1">Photo Prompt</h3>
              <p>
                "Waterproofing is extremely easy for us to estimate virtually — photos will let us give you a
                quote quickly."
              </p>
            </div>
          )}
        </div>
      );
    }

    if (div === 'Septics') {
      return (
        <div className="space-y-6">
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <h2 className="text-lg font-semibold mb-3 text-gray-900">Septics – Question Pack</h2>
            <h3 className="text-sm font-semibold mb-2 text-gray-900">Type of Request</h3>
            <select
              value={formData.septics.request_type}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  septics: { ...formData.septics, request_type: e.target.value },
                })
              }
              className="w-full border border-gray-300 rounded-md px-2 py-2 text-sm mb-3"
            >
              <option value="">Select...</option>
              <option value="New installation">New installation</option>
              <option value="Replacement of existing system">Replacement of existing system</option>
              <option value="System failure / backup / flooding">System failure / backup / flooding</option>
              <option value="Tank replacement only">Tank replacement only</option>
              <option value="Real estate condition">Real estate condition</option>
              <option value="Inspection or diagnosis">Inspection or diagnosis</option>
            </select>

            <div className="bg-white border border-gray-200 rounded-lg p-5 mb-4">
              <h3 className="text-sm font-semibold mb-2 text-gray-900">Summary of Issue</h3>
              <p className="text-xs text-gray-500 mb-2">Type a brief summary in your own words for the estimator.</p>
              <textarea
                value={formData.issue_summary}
                onChange={(e) => setFormData({ ...formData, issue_summary: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm h-24"
                placeholder="Brief summary in the admin's own words..."
              />
            </div>

            <div className="grid md:grid-cols-3 gap-4 text-sm">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Bedrooms</label>
                <input
                  type="number"
                  value={formData.septics.bedrooms}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      septics: { ...formData.septics, bedrooms: e.target.value },
                    })
                  }
                  className="w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Bathrooms</label>
                <input
                  type="number"
                  value={formData.septics.bathrooms}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      septics: { ...formData.septics, bathrooms: e.target.value },
                    })
                  }
                  className="w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Approx. square footage</label>
                <input
                  type="number"
                  value={formData.septics.square_footage}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      septics: { ...formData.septics, square_footage: e.target.value },
                    })
                  }
                  className="w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4 mt-4 text-sm">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Soil type</label>
                <input
                  type="text"
                  value={formData.septics.soil_type}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      septics: { ...formData.septics, soil_type: e.target.value },
                    })
                  }
                  className="w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
                  placeholder="Sandy, clay-like, mixed..."
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Property size</label>
                <input
                  type="text"
                  value={formData.septics.lot_size}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      septics: { ...formData.septics, lot_size: e.target.value },
                    })
                  }
                  className="w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
                  placeholder="Approximate lot size / acreage"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-xs font-medium text-gray-700 mb-1">Urgency notes</label>
              <textarea
                value={formData.septics.urgency_notes}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    septics: { ...formData.septics, urgency_notes: e.target.value },
                  })
                }
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm h-24"
                placeholder="Is the system backing up or just not functioning properly?"
              />
            </div>
          </div>

          {!hideDivisionPhotoPrompt && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 text-sm text-blue-900">
              <h3 className="font-semibold mb-1">Photo Prompt</h3>
              <p>
                "Septic estimates are much faster when we receive photos of the yard, tank area, and any issues.
                You can send them to {currentUser?.company?.email || 'our office'}."
              </p>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-5 text-sm text-yellow-900">
        <p>
          Select a division in Step 1 and confirm basic contact info in Step 2 to unlock the
          division-specific questions here.
        </p>
      </div>
    );
  };

  const renderPhotosStep = () => (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <h2 className="text-lg font-semibold mb-3 text-gray-900">Push for Photos &amp; Info</h2>
        {intakeType === 'phone' ? (
          <>
            <p className="text-sm text-gray-700 mb-3">
              Say this on <span className="font-semibold">every single call</span>:
            </p>
            <p className="text-sm text-gray-800 mb-3">
              "To help our estimator get you a quote faster, please send any photos, videos, or past reports to
              {currentUser?.company?.email || 'our office'}. The more information we receive, the sooner we can help."
            </p>
            <p className="text-xs text-gray-500 mb-2">If the customer resists:</p>
            <p className="text-sm text-gray-800 mb-4">
              "Totally understandable — even a single photo of the area helps us get started."
            </p>
          </>
        ) : (
          <p className="text-sm text-gray-700 mb-3">
            Ensure to request photos from the customer to provide to the estimator.
          </p>
        )}

        <div className="mt-2">
          <h3 className="text-xs font-semibold mb-2 text-gray-900">Photos Received?</h3>
          <select
            value={formData.photos_received}
            onChange={(e) => setFormData({ ...formData, photos_received: e.target.value })}
            className="w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
          >
            <option value="No">No</option>
            <option value="Customer to send">Customer to send</option>
            <option value="Yes">Yes</option>
          </select>
          <p className="text-[11px] text-gray-500 mt-2">
            Mark &quot;Yes&quot; once the customer has emailed photos/reports.
          </p>



        </div>
      </div>

      {!hidePhotosReminder && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 text-sm text-blue-900">
          <h3 className="font-semibold mb-1">Reminder</h3>
          <p>
            You&apos;ll choose the estimator and send the full intake (including any uploaded photos) to them in <span className="font-semibold">Step 5</span>.
          </p>
        </div>
      )}
    </div>
  );

// Customer Care - Phone Intake Archive (defined near App)

  const renderWrapupStep = () => (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        {intakeType === 'phone' && (
          <>
            <h2 className="text-lg font-semibold mb-3 text-gray-900">Ending the Call – Professional Wrap-Up</h2>
            <p className="text-sm text-gray-800 mb-3">
              "Thank you for calling. I&apos;ll forward everything to our estimator for review. They may
              reach out with follow-up questions or schedule an in-person visit if needed. Expect an update soon."
            </p>
            <p className="text-xs text-gray-500 mb-4">
              This makes your admin sound polished, confident, and structured.
            </p>
          </>
        )}

        <div className="mt-2">
          <h3 className="text-sm font-semibold mb-2 text-gray-900">Notes for Estimator</h3>
          <textarea
            value={formData.notes_for_estimator}
            onChange={(e) => setFormData({ ...formData, notes_for_estimator: e.target.value })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm h-32"
            placeholder="Any nuance from the call, concerns, tone, or expectations you want the estimator to know..."
          />
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-5 text-sm mb-4">
        <h3 className="font-semibold mb-2 text-gray-900">Upload Photos (Internal)</h3>
        <p className="text-xs text-gray-500 mb-3">
          Upload any photos the customer has already emailed or sent you. These will be attached to the
          intake email that goes to the estimator.
        </p>
        <div className="space-y-3 text-sm">
          <div>
            <label
              className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md cursor-pointer text-sm font-semibold"
            >
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={async (event) => {
                  const files = Array.from(event.target.files || []);
                  if (!files.length) return;
                  if (!intakeId) {
                    alert('Please wait for autosave to create the intake before uploading photos.');
                    event.target.value = '';
                    return;
                  }
                  const data = new FormData();
                  files.forEach((file) => data.append('files', file));
                  setIsUploadingPhotos(true);
                  try {
                    const res = await axios.post(`${API}/intakes/${intakeId}/upload-photos`, data, {
                      headers: { 'Content-Type': 'multipart/form-data' },
                    });
                    const uploaded = res.data.uploaded_files || [];
                    setFormData((prev) => ({ ...prev, uploaded_files: uploaded }));
                  } catch (err) {
                    console.error('Error uploading intake photos', err);
                    alert('Error uploading photos for this intake.');
                  } finally {
                    setIsUploadingPhotos(false);
                    event.target.value = '';
                  }
                }}
                className="hidden"
              />
              {isUploadingPhotos ? 'Uploading…' : 'Upload Photos'}
            </label>
          </div>
          <div className="text-xs text-gray-500">
            You can upload multiple images (JPG, PNG, etc.). Larger files may take a few seconds.
          </div>
          {formData.uploaded_files && formData.uploaded_files.length > 0 && (
            <div className="mt-2" data-testid="lead-intake-photos-thumbnails">
              <IntakePhotoThumbnails
                intakeId={intakeId}
                files={formData.uploaded_files}
                testIdPrefix="lead-intake-uploaded"
                onOpen={async (file) => {
                  if (!intakeId) return;
                  try {
                    const res = await axios.get(`${API}/intakes/${intakeId}/files/${file.id}`, {
                      responseType: 'blob',
                    });
                    const blobUrl = window.URL.createObjectURL(res.data);
                    window.open(blobUrl, '_blank');
                    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 5000);
                  } catch (err) {
                    console.error('Error opening intake photo', err);
                    const status = err?.response?.status;
                    if (status === 404) {
                      alert('Photo file not found on server. If this is an older intake, the original photo may have been lost during a deployment. Please re-upload it if possible.');
                    } else {
                      alert('Unable to open photo.');
                    }
                  }
                }}
              />
              <div className="text-[10px] text-gray-500 mt-2" data-testid="lead-intake-photos-note">
                Click a thumbnail to open the full-size image in a new tab.
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mt-4">
        <div className="bg-white border border-gray-200 rounded-lg p-5 text-sm">
          <h3 className="font-semibold mb-2 text-gray-900">Internal Workflow (Recommended)</h3>
          <ol className="list-decimal list-inside text-xs text-gray-700 space-y-1">
            <li>Complete this intake script and the division question pack.</li>
            <li>
              Categorize the lead as
              <span className="font-semibold"> High</span> (active failure / structural risk / flooding),
              <span className="font-semibold"> Medium</span> (moderate concern), or
              <span className="font-semibold"> Low</span> (quoting / non-urgent).
            </li>
            <li>
              Send details to the estimator with: name, phone, email, address, division, issue summary,
              answers to question pack, priority, and whether photos were received.
            </li>
          </ol>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-5 text-sm">
          <h3 className="font-semibold mb-2 text-gray-900">Quick Summary (for your own records)</h3>
          <ul className="text-xs text-gray-700 space-y-1">
            <li>
              <span className="font-semibold">Name:</span> {formData.name || 'Not entered'}
            </li>
            <li>
              <span className="font-semibold">Division:</span> {formData.division || 'Not selected'}
            </li>
            <li>
              <span className="font-semibold">Priority:</span> {formData.priority}
            </li>
            <li>
              <span className="font-semibold">Photos received:</span> {formData.photos_received}
            </li>
          </ul>
          <p className="text-[11px] text-gray-500 mt-3">
            {/* Autosave status */}
            <div className="flex items-center justify-end mt-2 mb-4 text-xs text-gray-500">
              {isSaving && <span>Saving intake...</span>}
              {!isSaving && lastSavedAt && (
                <span>Saved at {lastSavedAt.toLocaleTimeString()}</span>
              )}
              {saveError && (
                <span className="text-red-500 ml-2">{saveError}</span>
              )}
            </div>

            You can copy these details into an email or your estimator hub immediately after the call.
          </p>
        </div>
      </div>
    </div>
  );

  const currentStepKey = steps[activeStepIndex].key;

  let content;
  if (currentStepKey === 'intake') content = renderIntakeStep();
  else if (currentStepKey === 'contact') content = renderContactStep();
  else if (currentStepKey === 'division') content = renderDivisionStep();
  else if (currentStepKey === 'photos') content = renderPhotosStep();
  else content = renderWrapupStep();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white text-gray-800">
      <div className="mx-auto px-8 py-8 w-full">
        {/* Back button */}
        <div className="mb-6">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-sm"
          >
            ← Back
          </button>
        </div>

        {/* Header */}
        <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-1">
              {(currentUser?.company?.name || 'Company').toUpperCase()} Customer Intake System
            </h1>
            <p className="text-gray-600 text-sm max-w-2xl">
              Customer Intake System helps your admin team handle calls professionally,
              extract the right details, and set clear expectations for estimators.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            {/* Manual Save Button */}
            <button
              type="button"
              onClick={handleManualSave}
              disabled={isSaving}
              className="px-5 py-2.5 rounded-lg bg-green-600 text-white font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-colors text-sm whitespace-nowrap"
              data-testid="intake-save-btn"
            >
              {isSaving ? 'Saving...' : 'Save Intake'}
            </button>
          </div>
        </div>

        {/* Layout */}
        <div className="grid md:grid-cols-4 gap-6 items-start">
          {/* Step list */}
          <div className="md:col-span-1">
            <div className="bg-white border border-gray-200 rounded-lg p-4 sticky top-4 text-sm">
              <h2 className="text-xs font-semibold text-gray-500 mb-3 uppercase tracking-wide">
                Steps
              </h2>
              <ol className="space-y-2">
                {steps.map((step, index) => (
                  <li key={step.id}>
                    <button
                      type="button"
                      onClick={() => goToStep(index)}
                      className={`w-full text-left px-3 py-2 rounded-md text-xs sm:text-sm border transition-colors ${
                        index === activeStepIndex
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-800 border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {step.title}
                    </button>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          {/* Main content */}
          <div className="md:col-span-3">
            <div className="space-y-6">{content}</div>

            {/* Estimator selection and send controls - only on Step 5 */}
            {currentStepKey === 'wrapup' && (
              <div className="bg-white border border-gray-200 rounded-lg p-5 mt-4">
                <h3 className="text-sm font-semibold mb-2 text-gray-900">Send Intake to Estimator</h3>
                <p className="text-xs text-gray-500 mb-3">
                  Type an estimator email or pick a recent one, then send the full intake report.
                </p>
                {sendStatus === 'success' && (
                  <div className="mb-3 inline-flex items-center px-3 py-1.5 rounded-full bg-green-50 text-green-700 text-xs border border-green-200">
                    ✓ Intake sent to estimator. You can now click Complete to exit.
                  </div>
                )}
                {sendStatus === 'error' && (
                  <div className="mb-3 inline-flex items-center px-3 py-1.5 rounded-full bg-red-50 text-red-700 text-xs border border-red-200">
                    ⚠️ Failed to send intake. Please try again.
                  </div>
                )}
                <div className="grid md:grid-cols-[2fr,1fr] gap-4 items-end text-sm">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Estimator Email</label>
                    <input
                      list="known-estimators-list"
                      type="email"
                      value={estimatorInput}
                      onChange={(e) => {
                        const value = e.target.value;
                        setEstimatorInput(value);
                        setFormData((prev) => ({ ...prev, estimator_email: value }));
                      }}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      placeholder="estimator@example.com"
                    />
                    {knownEstimators.length > 0 && (
                      <datalist id="known-estimators-list">
                        {knownEstimators.map((email) => (
                          <option key={email} value={email} />
                        ))}
                      </datalist>
                    )}
                  </div>

                  <div className="flex flex-col md:flex-row gap-2 justify-end">
                    <button
                      type="button"
                      disabled={!intakeId || !formData.estimator_email}
                      onClick={async () => {
                        try {
                          const email = estimatorInput.trim();
                          setSendStatus(null);
                          if (!intakeId) {
                            alert('Please wait for autosave to complete before sending.');
                            return;
                          }
                          if (!email) {
                            alert('Please enter an estimator email.');
                            return;
                          }
                          await axios.post(`${API}/intakes/${intakeId}/send-to-estimator`, {
                            estimator_email: email,
                          });

                          // Remember this estimator email for future use
                          if (email && !knownEstimators.includes(email)) {
                            const updated = [...knownEstimators, email];
                            setKnownEstimators(updated);
                            try {
                              localStorage.setItem('knownEstimators', JSON.stringify(updated));
                            } catch (e) {
                              console.error('Failed to save knownEstimators', e);
                            }
                          }

                          setSendStatus('success');
                        } catch (err) {
                          console.error('Error sending intake to estimator', err);
                          setSendStatus('error');
                        }
                      }}
                      className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      Send
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation buttons */}
            <div className="flex justify-between items-center mt-8 pt-4 border-t border-gray-200 text-sm">
              <button
                type="button"
                onClick={handleBack}
                disabled={activeStepIndex === 0}
                className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-500">
                  Step {activeStepIndex + 1} of {steps.length}
                </span>
                <button
                  type="button"
                  onClick={handleManualSave}
                  disabled={isSaving}
                  className="px-4 py-2 rounded-md border-2 border-green-600 text-green-700 font-semibold hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                  data-testid="intake-save-btn-bottom"
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
              {currentStepKey !== 'wrapup' ? (
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={activeStepIndex === steps.length - 1}
                  className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              ) : (
                <button
                  type="button"
                  onClick={async () => {
                    if (!intakeId) {
                      alert('Intake is still autosaving. Please wait a moment and try again.');
                      return;
                    }
                    try {
                      await axios.post(`${API}/intakes/${intakeId}/sync-crm`);
                    } catch (err) {
                      console.error('Error syncing intake to CRM', err);
                      // We still allow completion even if CRM sync fails
                    }
                    alert('Intake completed. You can find it in the Intake Archive under Customer Care. A CRM lead has been created/updated.');
                    onBack();

                  }}
                  className="px-4 py-2 rounded-md bg-green-600 text-white hover:bg-green-700 text-sm"
                >
                  Complete Intake
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Lead Intake Module (shares backend with Phone Intake, but without scripts)
const LeadIntakeModule = (props) => {
  return (
    <PhoneIntakeModule
      {...props}
      hideScripts
      hideDivisionPhotoPrompt
      hidePhotosReminder
      intakeType="lead"
    />
  );
};




 

// Human Resources Home Page

// Phone Intake Archive
const PhoneIntakeArchive = ({ onBack, onOpenIntake }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deletingId, setDeletingId] = useState(null);


  const fetchArchive = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(`${API}/intakes`);
      setItems(res.data || []);
    } catch (err) {
      console.error('Error fetching intake archive', err);
      setError('Failed to load intake archive. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = items.filter((item) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      (item.name || '').toLowerCase().includes(term) ||
      (item.phone || '').toLowerCase().includes(term) ||
      (item.division || '').toLowerCase().includes(term) ||
      (item.intake_form_name || '').toLowerCase().includes(term) ||
      (item.estimator_email || '').toLowerCase().includes(term)
    );
  });

  const getIntakeTypeLabel = (item) => {
    const intakeType = item.intake_type || 'phone';
    if (intakeType === 'custom') return item.intake_form_name || 'Custom Form';
    if (intakeType === 'lead') return 'Lead Intake';
    return 'Guided Intake';
  };

  useEffect(() => {
    fetchArchive();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800">
      <div className="w-full max-w-none mx-auto px-1 sm:px-3 lg:px-4 py-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6" data-testid="phone-intake-archive-header">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-sm"
              data-testid="phone-intake-archive-back"
            >
              ← Back
            </button>
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight" data-testid="phone-intake-archive-title">Intake Archive</h1>
          </div>
          <div className="flex flex-col md:flex-row gap-3 md:items-center w-full md:w-auto">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, phone, division, estimator..."
              className="flex-1 md:w-64 border border-gray-300 rounded-lg px-3 py-2 text-sm"
              data-testid="phone-intake-archive-search"
            />
            <button
              type="button"
              onClick={fetchArchive}
              className="text-sm px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100"
              data-testid="phone-intake-archive-refresh"
            >
              Refresh
            </button>
          </div>
        </div>

        {loading && (
          <div className="text-center py-10 text-gray-500 text-sm" data-testid="phone-intake-archive-loading">Loading intakes…</div>
        )}

        {error && !loading && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700" data-testid="phone-intake-archive-error">{error}</div>
        )}

        {!loading && !error && filteredItems.length === 0 && (
          <div className="text-center py-12 text-gray-500 text-sm" data-testid="phone-intake-archive-empty">No matching intakes found.</div>
        )}

        {!loading && !error && filteredItems.length > 0 && (
          <>
          <div className="space-y-3 lg:hidden" data-testid="phone-intake-archive-mobile-list">
            {filteredItems.map((item) => (
              <div
                key={`mobile-${item.id}`}
                className="bg-white rounded-xl shadow ring-1 ring-gray-200 p-3"
                data-testid={`phone-intake-mobile-card-${item.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 break-words" data-testid={`phone-intake-mobile-customer-${item.id}`}>
                      {item.name || 'N/A'}
                    </p>
                    <p className="text-xs text-gray-600 mt-0.5" data-testid={`phone-intake-mobile-date-${item.id}`}>
                      {item.created_date ? new Date(item.created_date).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-[11px] font-medium whitespace-nowrap ${
                      item.status === 'completed'
                        ? 'bg-green-50 text-green-700 border border-green-200'
                        : 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                    }`}
                    data-testid={`phone-intake-mobile-status-${item.id}`}
                  >
                    {item.status === 'completed' ? 'Completed' : 'In Progress'}
                  </span>
                </div>

                <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
                  <div data-testid={`phone-intake-mobile-phone-${item.id}`}>
                    <p className="text-gray-500">Phone</p>
                    <p className="text-gray-800 break-words">{item.phone || 'N/A'}</p>
                  </div>
                  <div data-testid={`phone-intake-mobile-division-${item.id}`}>
                    <p className="text-gray-500">Division</p>
                    <p className="text-gray-800 break-words">{item.division || 'N/A'}</p>
                  </div>
                  <div className="col-span-2" data-testid={`phone-intake-mobile-type-${item.id}`}>
                    <p className="text-gray-500">Type/Form</p>
                    <p className="text-gray-800 break-words">{getIntakeTypeLabel(item)}</p>
                  </div>
                  <div className="col-span-2" data-testid={`phone-intake-mobile-estimator-${item.id}`}>
                    <p className="text-gray-500">Estimator</p>
                    <p className="text-gray-800 break-all">{item.estimator_email || '—'}</p>
                  </div>
                </div>

                <div className="mt-3 flex gap-2" data-testid={`phone-intake-mobile-card-actions-${item.id}`}>
                  <button
                    type="button"
                    onClick={() => {
                      if ((item.intake_type || 'phone') !== 'custom') {
                        onOpenIntake(item);
                      }
                    }}
                    className="flex-1 inline-flex justify-center items-center px-3 py-2 rounded-md text-xs font-medium border border-blue-600 text-blue-600 bg-white hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid={`phone-intake-mobile-open-${item.id}`}
                    disabled={(item.intake_type || 'phone') === 'custom'}
                  >
                    {(item.intake_type || 'phone') === 'custom' ? 'Saved' : 'Open'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeletingId(item.id)}
                    className="inline-flex justify-center items-center px-3 py-2 rounded-md text-xs font-medium border border-red-500 text-red-600 bg-white hover:bg-red-50"
                    data-testid={`phone-intake-mobile-delete-${item.id}`}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="hidden lg:block bg-white rounded-xl shadow ring-1 ring-gray-200 overflow-x-visible" data-testid="phone-intake-archive-table-wrapper">
            <table className="w-full table-fixed border-collapse text-sm" data-testid="phone-intake-archive-table">
              <colgroup>
                <col style={{ width: '8%' }} />
                <col style={{ width: '17%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '19%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '8%' }} />
                <col style={{ width: '10%' }} />
              </colgroup>
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-1.5 lg:px-2 py-2 text-left font-semibold text-gray-700">Date</th>
                  <th className="px-1.5 lg:px-2 py-2 text-left font-semibold text-gray-700">Customer</th>
                  <th className="px-1.5 lg:px-2 py-2 text-left font-semibold text-gray-700">Phone</th>
                  <th className="px-1.5 lg:px-2 py-2 text-left font-semibold text-gray-700">Division</th>
                  <th className="px-1.5 lg:px-2 py-2 text-left font-semibold text-gray-700">Type/Form</th>
                  <th className="px-1.5 lg:px-2 py-2 text-left font-semibold text-gray-700">Estimator</th>
                  <th className="px-1.5 lg:px-2 py-2 text-left font-semibold text-gray-700">Status</th>
                  <th className="hidden sm:table-cell px-1.5 lg:px-2 py-2 text-right font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item) => (
                  <tr
                    key={item.id}
                    className="border-t border-gray-100 hover:bg-blue-50 cursor-pointer sm:cursor-default"
                    data-testid={`phone-intake-row-${item.id}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      if ((item.intake_type || 'phone') !== 'custom' && typeof window !== 'undefined' && window.innerWidth < 640) {
                        onOpenIntake(item);
                      }
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter' && event.key !== ' ') return;
                      if ((item.intake_type || 'phone') !== 'custom' && typeof window !== 'undefined' && window.innerWidth < 640) {
                        event.preventDefault();
                        onOpenIntake(item);
                      }
                    }}
                  >
                    <td className="px-1.5 lg:px-2 py-2 text-gray-800 align-top whitespace-nowrap" data-testid={`phone-intake-date-${item.id}`}>
                      {item.created_date ? new Date(item.created_date).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-1.5 lg:px-2 py-2 text-gray-800 align-top break-words" data-testid={`phone-intake-customer-${item.id}`}>{item.name || 'N/A'}</td>
                    <td className="px-1.5 lg:px-2 py-2 text-gray-800 align-top break-words" data-testid={`phone-intake-phone-${item.id}`}>{item.phone || 'N/A'}</td>
                    <td className="px-1.5 lg:px-2 py-2 text-gray-800 align-top break-words" data-testid={`phone-intake-division-${item.id}`}>{item.division || 'N/A'}</td>
                    <td className="px-1.5 lg:px-2 py-2 text-gray-800 align-top break-words" data-testid={`phone-intake-type-${item.id}`}>
                      {getIntakeTypeLabel(item)}
                    </td>
                    <td className="px-1.5 lg:px-2 py-2 text-gray-800 align-top break-all text-xs sm:text-sm" data-testid={`phone-intake-estimator-${item.id}`}>{item.estimator_email || '—'}</td>
                    <td className="px-1.5 lg:px-2 py-2 text-gray-800 align-top" data-testid={`phone-intake-status-cell-${item.id}`}>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            item.status === 'completed'
                              ? 'bg-green-50 text-green-700 border border-green-200'
                              : 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                          }`}
                          data-testid={`phone-intake-status-${item.id}`}
                        >
                          {item.status === 'completed' ? 'Completed' : 'In Progress'}
                        </span>
                        <div className="flex items-center gap-2 sm:hidden" data-testid={`phone-intake-mobile-actions-${item.id}`}>
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              setDeletingId(item.id);
                            }}
                            className="inline-flex items-center px-2.5 py-1 rounded-md text-[11px] font-semibold border border-red-500 text-red-600 bg-white hover:bg-red-50"
                            data-testid={`phone-intake-delete-mobile-${item.id}`}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </td>
                    <td className="hidden sm:table-cell px-1.5 lg:px-2 py-2 text-right align-top">
                      <div className="inline-flex flex-col lg:flex-row items-end lg:items-center gap-1.5">
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            if ((item.intake_type || 'phone') !== 'custom') {
                              onOpenIntake(item);
                            }
                          }}
                          className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium border border-blue-600 text-blue-600 bg-white hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          data-testid={`phone-intake-open-${item.id}`}
                          disabled={(item.intake_type || 'phone') === 'custom'}
                        >
                          {(item.intake_type || 'phone') === 'custom' ? 'Saved' : 'Open'}
                        </button>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            setDeletingId(item.id);
                          }}
                          className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium border border-red-500 text-red-600 bg-white hover:bg-red-50"
                          data-testid={`phone-intake-delete-${item.id}`}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {deletingId && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-start sm:items-center justify-center z-50 p-4 pt-12 sm:pt-4 overflow-y-auto" data-testid="phone-intake-delete-modal">
              <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm">
                <h2 className="text-lg font-semibold mb-2" data-testid="phone-intake-delete-title">Delete Intake</h2>
                <p className="text-sm text-gray-600 mb-4" data-testid="phone-intake-delete-description">
                  Are you sure you want to permanently delete this intake from the archive? This action cannot be undone.
                </p>
                <div className="flex justify-end gap-2 text-sm">
                  <button
                    type="button"
                    onClick={() => setDeletingId(null)}
                    className="px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100"
                    data-testid="phone-intake-delete-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await axios.delete(`${API}/intakes/${deletingId}`);
                        setDeletingId(null);
                        fetchArchive();
                      } catch (err) {
                        console.error('Error deleting intake', err);
                        setDeletingId(null);
                      }
                    }}
                    className="px-3 py-1.5 rounded-md bg-red-600 text-white hover:bg-red-700"
                    data-testid="phone-intake-delete-confirm"
                  >
                    Yes, Delete
                  </button>
                </div>
              </div>
            </div>
          )}
          </>
        )}
      </div>
    </div>
  );
};


// Event Details/Edit Modal Component

export { CustomerCareHomePage, PhoneIntakeModule, LeadIntakeModule, PhoneIntakeArchive };
